package org.example.project.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.outlined.ContentCopy
import androidx.compose.material.icons.outlined.Visibility
import androidx.compose.material.icons.outlined.VisibilityOff
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.FirebaseManager
import org.example.project.PrimaryColor
import org.example.project.LicenseKeyDocument
import org.example.project.LicenseKeyStatus
import org.example.project.RevokeLicenseKeyResult

@Composable
fun LicenseKeyListSection(
    licenseKeys: List<LicenseKeyDocument>,
    isLoadingCodes: Boolean,
    nextCursor: String?,
    selectedStatus: LicenseKeyStatus?,
    onStatusChange: (LicenseKeyStatus?) -> Unit,
    onLoadCodes: (Boolean) -> Unit
) {
    val scope = rememberCoroutineScope()
    var revokingCodes by remember { mutableStateOf<Set<String>>(emptySet()) }

    Card(
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(24.dp).fillMaxWidth()) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "My License Keys",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF64748B) // Slate 500
                )
                IconButton(onClick = { onLoadCodes(true) }) {
                    Icon(Icons.Default.Refresh, contentDescription = "Refresh", tint = Color(0xFF64748B))
                }
            }
            Spacer(modifier = Modifier.height(16.dp))
            
            // Status Filter Row
            FlowRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                StatusFilterChip(
                    label = "All",
                    isSelected = selectedStatus == null,
                    onClick = { onStatusChange(null) }
                )
                LicenseKeyStatus.entries.forEach { status ->
                    val displayLabel = when(status) {
                        LicenseKeyStatus.ACTIVE -> "Available"
                        LicenseKeyStatus.CLAIMED -> "Applied"
                        LicenseKeyStatus.REVOKED -> "Revoked"
                    }
                    StatusFilterChip(
                        label = displayLabel,
                        isSelected = selectedStatus == status,
                        onClick = { onStatusChange(status) }
                    )
                }
            }
            
            Spacer(modifier = Modifier.height(16.dp))

            if (isLoadingCodes) {
                Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(modifier = Modifier.size(32.dp), color = PrimaryColor)
                }
            } else if (licenseKeys.isEmpty()) {
                Text("No license keys found.", color = Color(0xFF94A3B8), style = MaterialTheme.typography.bodySmall)
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                    licenseKeys.forEach { code ->
                        key(code.key_id) {
                            val isRevoking = revokingCodes.contains(code.key_id)
                            LicenseKeyItem(
                                code = code,
                                isRevoking = isRevoking,
                                onRevoke = {
                                    scope.launch {
                                        revokingCodes = revokingCodes + code.key_id
                                        val result = FirebaseManager.revokeLicenseKey(code.key_id, code.key_sn)
                                        if (result is RevokeLicenseKeyResult.Success) {
                                            onLoadCodes(true)
                                        }
                                        revokingCodes = revokingCodes - code.key_id
                                    }
                                }
                            )
                        }
                    }
                }
                
                // Load more trigger
                if (!nextCursor.isNullOrBlank()) {
                    Box(
                        modifier = Modifier.fillMaxWidth().padding(16.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        // Assuming isLoadingCodes is for initial load or refresh, we might not have a separate 
                        // flag for pagination loading here, so we show the button.
                        // However, we can use the isMoreLoading state from the ViewModel if we had it. 
                        // For now, a manual click triggers the load.
                        TextButton(
                            onClick = { onLoadCodes(false) }
                        ) {
                            Text(
                                "Load More",
                                color = PrimaryColor,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun StatusFilterChip(label: String, isSelected: Boolean, onClick: () -> Unit) {
    val containerColor = if (isSelected) PrimaryColor.copy(alpha = 0.1f) else Color(0xFFF1F5F9)
    val contentColor = if (isSelected) PrimaryColor else Color(0xFF64748B)
    val borderColor = if (isSelected) PrimaryColor.copy(alpha = 0.5f) else Color(0xFFE2E8F0)

    Surface(
        onClick = onClick,
        color = containerColor,
        shape = MaterialTheme.shapes.extraSmall,
        border = BorderStroke(1.dp, borderColor)
    ) {
        Text(
            text = label,
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
            style = MaterialTheme.typography.labelSmall,
            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
            color = contentColor
        )
    }
}

@Composable
fun LicenseKeyItem(code: LicenseKeyDocument, isRevoking: Boolean, onRevoke: () -> Unit) {
    val (statusColor, statusBg, statusText) = when (code.status) {
        LicenseKeyStatus.ACTIVE -> Triple(Color(0xFF16A34A), Color(0xFFF0FDF4), "AVAILABLE")
        LicenseKeyStatus.CLAIMED -> Triple(Color(0xFF64748B), Color(0xFFF8FAFC), "APPLIED")
        LicenseKeyStatus.REVOKED -> Triple(Color(0xFFDC2626), Color(0xFFFEF2F2), "REVOKED")
    }

    var pinVisible by remember { mutableStateOf(false) }

    Surface(
        color = Color(0xFFF8FAFC), // Slate 50
        shape = MaterialTheme.shapes.small,
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            // Header Row: Product Name & Status Badge
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "${code.bonus_units} Volume Licenses",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = PrimaryColor
                )
                
                Surface(
                    color = statusBg,
                    shape = MaterialTheme.shapes.small,
                    border = BorderStroke(1.dp, statusColor.copy(alpha = 0.2f))
                ) {
                    Text(
                        text = statusText,
                        color = statusColor,
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.labelSmall,
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                    )
                }
            }
            
            Spacer(modifier = Modifier.height(16.dp))
            
            // SN Row
            Text(
                text = "Serial Number:",
                style = MaterialTheme.typography.labelSmall,
                color = Color(0xFF94A3B8)
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = code.key_sn,
                    style = MaterialTheme.typography.bodyMedium.copy(fontFamily = androidx.compose.ui.text.font.FontFamily.Monospace),
                    color = Color(0xFF334155),
                    modifier = Modifier.weight(1f)
                )
                IconButton(
                    onClick = {
                        try {
                            window.navigator.clipboard.writeText(code.key_sn)
                        } catch (e: Exception) {
                            println("Clipboard not supported or blocked: ${e.message}")
                        }
                    },
                    modifier = Modifier.size(32.dp)
                ) {
                    Icon(
                        imageVector = Icons.Outlined.ContentCopy,
                        contentDescription = "Copy SN",
                        tint = PrimaryColor,
                        modifier = Modifier.size(16.dp)
                    )
                }
            }
            
            Spacer(modifier = Modifier.height(12.dp))
            
            // PIN Row
            Text(
                text = "Security PIN:",
                style = MaterialTheme.typography.labelSmall,
                color = Color(0xFF94A3B8)
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                val displayPin = if (pinVisible) {
                    code.key_pin
                } else {
                    "•••• •••• •••• " + code.key_pin.takeLast(4)
                }
                Text(
                    text = displayPin,
                    style = MaterialTheme.typography.bodyMedium.copy(fontFamily = androidx.compose.ui.text.font.FontFamily.Monospace),
                    color = Color(0xFF334155),
                    modifier = Modifier.weight(1f)
                )
                Row {
                    IconButton(
                        onClick = { pinVisible = !pinVisible },
                        modifier = Modifier.size(32.dp)
                    ) {
                        Icon(
                            imageVector = if (pinVisible) Icons.Outlined.VisibilityOff else Icons.Outlined.Visibility,
                            contentDescription = if (pinVisible) "Hide PIN" else "Show PIN",
                            tint = Color(0xFF64748B),
                            modifier = Modifier.size(18.dp)
                        )
                    }
                    IconButton(
                        onClick = {
                            try {
                                window.navigator.clipboard.writeText(code.key_pin)
                            } catch (e: Exception) {
                                println("Clipboard not supported or blocked: ${e.message}")
                            }
                        },
                        modifier = Modifier.size(32.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Outlined.ContentCopy,
                            contentDescription = "Copy PIN",
                            tint = PrimaryColor,
                            modifier = Modifier.size(16.dp)
                        )
                    }
                }
            }

            // Footer info (Time info or Revoke action)
            Spacer(modifier = Modifier.height(16.dp))
            HorizontalDivider(color = Color(0xFFE2E8F0))
            Spacer(modifier = Modifier.height(12.dp))
            
            val timeText = when (code.status) {
                LicenseKeyStatus.ACTIVE -> "Generated at: ${code.created_at}"
                LicenseKeyStatus.CLAIMED -> "Applied at: ${code.claimed_at ?: "Unknown"}"
                LicenseKeyStatus.REVOKED -> "Revoked at: ${code.revoked_at ?: "Unknown"}"
            }
            Text(
                text = timeText,
                style = MaterialTheme.typography.labelSmall,
                color = Color(0xFF94A3B8)
            )

            if (code.status == LicenseKeyStatus.ACTIVE) {
                Spacer(modifier = Modifier.height(16.dp))
                Button(
                    onClick = onRevoke,
                    enabled = !isRevoking,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = Color(0xFFFEE2E2), // Red 100
                        contentColor = Color(0xFFDC2626)  // Red 600
                    ),
                    contentPadding = PaddingValues(horizontal = 16.dp, vertical = 0.dp),
                    modifier = Modifier.height(36.dp).fillMaxWidth(),
                    shape = MaterialTheme.shapes.small,
                    elevation = ButtonDefaults.buttonElevation(0.dp)
                ) {
                    if (isRevoking) {
                        CircularProgressIndicator(modifier = Modifier.size(16.dp), color = Color(0xFFDC2626), strokeWidth = 2.dp)
                    } else {
                        Text("Revoke Access", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }
}
